var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useMemo } from "react";
import { usePendingPick } from "hooks/use-pending-pick";
import { selectors, useAppSelector } from "store";
import { getCupChoiceColumns, hasCupChoices, resolveCupChoice, } from "utility/cup-choices";
/*
 * Picking a suction cup within its family column by column — size, material,
 * hardness, bellows — in both cup pickers (the sidebar drawer and the
 * in-scene overlay), the way `usePumpFamilySelection` picks pumps.
 *
 * `openFamilies` are the families the picker has open, `selectedCupOptions`
 * the cups the selected nodes `nodeIds` have; `commitCup` puts a cup on those
 * nodes. The family's cups are taken in Tacton's order, not the list's sorted
 * one, as the materials are offered in Tacton's order.
 * The columns are only offered (`hasCupChoices`) while one family is open and
 * Tacton sends its cups' sizes; otherwise the pickers list the family's cups.
 */
export var useCupChoice = function (openFamilies, selectedCupOptions, commitCup, nodeIds) {
    var _a;
    var suctionCupComponents = (_a = useAppSelector(selectors.selectSystemDataDefineParameters)) === null || _a === void 0 ? void 0 : _a.suctionCupComponents;
    var validCups = useMemo(function () { return Object.values(suctionCupComponents !== null && suctionCupComponents !== void 0 ? suctionCupComponents : {}).filter(function (cup) { return cup.valid; }); }, [suctionCupComponents]);
    var familyCups = useMemo(function () { return validCups.filter(function (cup) { return openFamilies.includes(cup.cupFamily); }); }, [validCups, openFamilies]);
    var _b = usePendingPick(validCups, selectedCupOptions, commitCup, nodeIds), selectedCup = _b.picked, commit = _b.commit, isPicked = _b.isPicked;
    /* Puts the cup of `cups` that best fits `wanted` on the selected nodes,
     * unless they all have it already. */
    var pickCupChoice = useCallback(function (cups, wanted) {
        var cup = resolveCupChoice(cups, wanted);
        if (cup && !isPicked(cup.value))
            commit(cup.value);
    }, [isPicked, commit]);
    /* Opening another family picks its first cup right away — the top option
     * of each column — so the user only has to change the columns they care
     * about. Opening the cup's own family keeps the cup, and a family without
     * columns leaves the pick to its list. */
    var pickCupFamily = useCallback(function (family) {
        if ((selectedCup === null || selectedCup === void 0 ? void 0 : selectedCup.cupFamily) === family)
            return;
        var cups = validCups.filter(function (cup) { return cup.cupFamily === family; });
        if (hasCupChoices(cups))
            pickCupChoice(cups, {});
    }, [selectedCup, validCups, pickCupChoice]);
    var cupChoiceColumns = useMemo(function () { return getCupChoiceColumns(familyCups, selectedCup); }, [familyCups, selectedCup]);
    var cupChoice = useMemo(function () {
        var current = cupChoiceColumns.selected;
        return __assign(__assign({}, cupChoiceColumns), { selectedCup: selectedCup, onSelect: function (key, value) {
                var _a;
                return pickCupChoice(familyCups, __assign(__assign({}, current), (_a = {}, _a[key] = value, _a)));
            } });
    }, [cupChoiceColumns, selectedCup, pickCupChoice, familyCups]);
    return {
        hasCupChoices: openFamilies.length === 1 && hasCupChoices(familyCups),
        cupChoice: cupChoice,
        pickCupFamily: pickCupFamily,
        commitCup: commit,
    };
};
